import { useCallback, useEffect } from 'react'

import { FrontCameraProfile } from '@editor/interfaces/camera'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useCanvasGrid from '@editor/pages/projects/blueprint-editor/objects/CanvasGrid/hooks/useCanvasGrid'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Camera, Material, Matrix4, Mesh, ShaderMaterial, Vector3 } from 'three'

const GRID_OBJECT_NAME = 'camera-grid'

const useGridMesh = () => {
  // Hooks
  const { orthographicCamera } = useBlueprintEditorContext()

  // Store
  const pcdBoundingSphereRadius = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingSphereRadius)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const gridOpacity = useSelector((state: RootState) => state.blueprint.editor.gridOpacity)

  /**
   * Make grid mesh
   */
  const { makeGridMesh } = useCanvasGrid(pcdBoundingSphereRadius * 4, gridOpacity)

  /**
   * Clear existing grid from camera
   */
  const clearGridFromCamera = useCallback((camera: Camera) => {
    const existingGrid = camera.children.find((child) => child.name === GRID_OBJECT_NAME)
    if (existingGrid) {
      camera.remove(existingGrid)
    }
  }, [])

  /**
   * Attach grid mesh to the active camera
   */
  const addGridToCamera = useCallback(
    (camera: Camera, profile: FrontCameraProfile) => {
      const { target } = profile

      // clear any existing grid first
      clearGridFromCamera(camera)

      const gridMesh = makeGridMesh(
        GRID_OBJECT_NAME,
        new Matrix4().setPosition(new Vector3(...target).applyMatrix4(camera.matrixWorldInverse)), // need to be local to camera
      )
      camera.add(gridMesh)

      return () => {
        camera.remove(gridMesh)
      }
    },
    [makeGridMesh, clearGridFromCamera],
  )

  /**
   * Whenever we enter 3D mode, hide the grid.
   * This is only to facilitate transitioning to mask region tool.
   * The removal of grid is handled on completion or cancellation of blueprint creation.
   */
  useEffect(() => {
    const grid = orthographicCamera.current?.children.find((child) => child.name === GRID_OBJECT_NAME)
    if (!grid) return

    if (grid instanceof Mesh) {
      const material = grid.material as Material | Material[]

      if (material instanceof ShaderMaterial && 'gridOpacity' in material.uniforms) {
        material.uniforms.gridOpacity.value = gridOpacity
      }
    }

    grid.visible = mode === Mode.Editor2D && gridOpacity > 0
  }, [mode, gridOpacity, orthographicCamera])

  return {
    addGridToCamera,
    clearGridFromCamera,
  }
}

export default useGridMesh
